import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { NavigationService } from "@services/navigation.service";
import { IUnit } from "@interfaces/IPeripheral";
import { CommissionService } from "../../_routes/commissions/commission.service";
import "./device-summary.component.scss";
import { FoundationNavigationModule } from "../../foundation.navigation";
import { SimService } from "../../_services/sim.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { finalize, map, switchMap } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { ISim } from "@app/interfaces/ISim";
import { ProductVendor } from "@app/main/components/videos/enums/CameraProductType";
import { SimMappingService } from "@app/main/modules/foundation/_services/sim-mapping.service";
import { ISerialSearchResponse } from "@app/main/modules/foundation/_models/ISerialSearchResponse";
import { AccountFeatures } from "@app/enum/account/AccountFeature";
import { SimMappingType } from "@app/interfaces/ISimMapping";

@UntilDestroy()
@Component({
    selector: "device-summary",
    template: require("./device-summary.component.html")
})
export class DeviceSummaryComponent implements OnChanges {
    @Input() serial: string;
    @Input() unit: IUnit;
    @Input() static: boolean = false;
    @Input() disableSerialLink: boolean;

    // ---

    sim: ISim;
    showSimDetail = false;
    loading = false;

    constructor(
        private readonly commissionService: CommissionService,
        private readonly navigationService: NavigationService,
        private readonly simMappingService: SimMappingService,
        private readonly simService: SimService) {
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['unit'] && !this.static && this.unit.Serial) {
            this.loadData();
        }
    }

    serialClicked(unit: IUnit, $event: MouseEvent) {
        $event.preventDefault();

        if (!unit.Id) {
            return;
        }

        if (unit.Imei) {
            this.navigationService.go(`${FoundationNavigationModule.Units}.view`, { unitSerial: unit.Serial });
        }
        else if (this.isCameraProduct(unit)) {
            this.navigationService.go(`${FoundationNavigationModule.Cameras}.view`, { serial: unit.Serial });
        }
        else {
            this.navigationService.go(`${FoundationNavigationModule.Peripherals}.view`, { serial: unit.Serial });
        }
    }

    openSimDetails(): void {
        this.showSimDetail = true;
    }

    closeSimDetails(): void {
        this.showSimDetail = false;
    }

    onNewSimAdded(sim: ISim): void {
        this.sim = sim;
    }

    get shouldShowSimAssign(): boolean {
        return !this.sim && this.unit?.Product?.GroupId !== ProductVendor.TachoSys && this.unit?.Product?.GroupId !== ProductVendor.SevenTelematics;
    }

    // ---

    private loadData(): void {

        this.loading = true;

        this.updateUnitOrPeripheralLabel(this.unit).pipe(
            untilDestroyed(this),
            switchMap(unitSearchResponse => {
                if (!!unitSearchResponse?.Unit) {
                    this.unit.Product = unitSearchResponse.Product;
                    return this.simMappingService.getSimMappings({ Serial: unitSearchResponse.Unit.Serial, IsActive: true, SimMappingType: SimMappingType.Unit })
                }

                if (!!unitSearchResponse?.Peripheral) {
                    return this.simMappingService.getSimMappings({ Serial: unitSearchResponse.Peripheral.Serial, IsActive: true, SimMappingType: SimMappingType.Peripheral })
                }

                return of(null);
            }),
            switchMap(simMappings => {
                if (!simMappings?.length) {
                    return of(false);
                }

                return this.simService.getSims({ Id: simMappings[0].SimId });
            }),
            map(sims => {

                this.sim = sims[0];
                return sims[0];
            }),
            finalize(() => this.loading = false))
            .subscribe();
    }

    private isCameraProduct(unit: IUnit): boolean {
        return (unit.Product.Features & AccountFeatures.Camera) === AccountFeatures.Camera;
    }

    private updateUnitOrPeripheralLabel(p: IUnit): Observable<ISerialSearchResponse> {
        return this.commissionService.getUnitOrPeripheralBySerial(p.Serial).pipe(
            map(unitSearchResponse => {
                unitSearchResponse.name = this.commissionService.createUnitLabel(unitSearchResponse);
                return unitSearchResponse;
            })
        )
    }
}
